import { motion } from "framer-motion";
import { ShieldCheck, Stethoscope, HeartHandshake, Phone } from "lucide-react";
import { PIKKO } from "../../lib/pikko";

const trust = [
  { icon: Stethoscope, text: "Professional Veterinary Care" },
  { icon: ShieldCheck, text: "Modern Medical Approach" },
  { icon: HeartHandshake, text: "Trusted Pet Wellness Service" },
];

const ease = [0.2, 0.8, 0.2, 1];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Decorative blobs */}
      <div
        aria-hidden
        className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full opacity-50 blur-3xl"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, rgba(217,140,63,0.28), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(138,107,82,0.22), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left: text */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[var(--pikko-cream-warm)] border border-[rgba(138,107,82,0.2)] mb-7"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--pikko-orange)] animate-pulse" />
            <span className="text-[11px] tracking-[0.22em] uppercase text-[var(--pikko-brown-deep)] font-medium">
              Compassionate Care · Happy Pets
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease }}
            className="font-serif text-[40px] leading-[1.05] sm:text-5xl lg:text-[68px] lg:leading-[1.02] tracking-tight text-[var(--pikko-brown-ink)]"
          >
            Professional{" "}
            <span className="italic text-[var(--pikko-orange)]">
              Veterinary
            </span>{" "}
            <br className="hidden sm:block" />
            &amp; Pet Wellness Center
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25, ease }}
            className="mt-7 max-w-xl text-[15px] sm:text-base leading-relaxed text-[var(--pikko-brown-deep)]/85"
          >
            Praktek Dokter Hewan di Jatiasih Bekasi dengan layanan poliklinik,
            rawat inap, emergency care, pet hotel, dan grooming profesional.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease }}
            className="mt-9 flex flex-wrap gap-3.5"
          >
            <a
              href={PIKKO.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-appointment-btn"
              className="btn-primary"
            >
              Book Appointment
            </a>
            <a
              href={PIKKO.whatsappEmergency}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-emergency-btn"
              className="btn-ghost"
            >
              <Phone className="w-4 h-4" /> Emergency Contact
            </a>
          </motion.div>

          {/* Trust badges (floating) */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.12, delayChildren: 0.55 } },
            }}
            className="mt-12 grid sm:grid-cols-3 gap-3.5"
          >
            {trust.map((t, i) => (
              <motion.div
                key={t.text}
                variants={{
                  hidden: { opacity: 0, y: 14 },
                  show: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.7, ease }}
                data-testid={`trust-badge-${i}`}
                className="glass-cream rounded-2xl px-4 py-3.5 flex items-center gap-3"
              >
                <div className="w-9 h-9 rounded-full bg-[var(--pikko-orange)]/10 flex items-center justify-center">
                  <t.icon className="w-4.5 h-4.5 text-[var(--pikko-orange-dark)]" />
                </div>
                <p className="text-[12.5px] leading-snug text-[var(--pikko-brown-deep)] font-medium">
                  {t.text}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Right: doctor portrait */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease }}
          className="lg:col-span-5 relative"
        >
          <div className="relative">
            <div
              aria-hidden
              className="absolute -inset-6 rounded-[40px] -z-10"
              style={{
                background:
                  "linear-gradient(135deg, rgba(217,140,63,0.18), rgba(138,107,82,0.08))",
              }}
            />
            <div className="relative aspect-[4/3] sm:aspect-[5/4] lg:aspect-[4/5] rounded-[32px] overflow-hidden shadow-[0_40px_100px_-40px_rgba(46,34,24,0.4)] border border-[rgba(138,107,82,0.15)]">
              <img
                src="/images/hero.webp"
                alt="Pikko Pet Care — kucing di area reception klinik"
                width="800"
                height="640"
                className="w-full h-full object-cover warm-grade"
                style={{ objectPosition: "30% 55%" }}
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 50%, rgba(46,34,24,0.18) 100%)",
                }}
              />
            </div>

            {/* Floating credential card */}
            <motion.div
              initial={{ opacity: 0, x: -20, y: 10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease }}
              className="absolute -left-4 sm:-left-8 bottom-8 glass-light rounded-2xl p-4 pr-5 shadow-[0_24px_60px_-30px_rgba(46,34,24,0.35)] flex items-center gap-3"
              data-testid="hero-cred-card"
            >
              <div className="w-11 h-11 rounded-full bg-[var(--pikko-brown-deep)] flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-[var(--pikko-cream)]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pikko-brown)] font-semibold">
                  We Care
                </div>
                <div className="font-serif text-[15px] text-[var(--pikko-brown-ink)] leading-tight">
                  They Deserve.
                </div>
              </div>
            </motion.div>

            {/* Top-right floating spec */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85, ease }}
              className="absolute -right-3 top-8 glass-light rounded-2xl px-4 py-3 shadow-[0_24px_60px_-30px_rgba(46,34,24,0.35)]"
              data-testid="hero-hours-card"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pikko-brown)] font-semibold">
                Open Daily
              </div>
              <div className="font-serif text-[16px] text-[var(--pikko-brown-ink)]">
                09.00 – 19.00
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
